import { useEffect, useRef } from 'react';
import { Button } from '../atoms';

/**
 * RichTextField — a small WYSIWYG editor for an email body (ATA part 3 §6: an information request
 * reads as personal outreach, so the operator edits the rendered template as text, not as HTML).
 * Bold, italic and bullets through the browser's editing commands; the value is the element's HTML.
 * Controlled from the outside: `value` is written into the editor only when it changes from a
 * source other than typing, so the caret never jumps mid-sentence.
 */
export function RichTextField({ value, onChange, disabled, minHeight = 220, ariaLabel = 'Email body' }: {
  value: string; onChange: (html: string) => void; disabled?: boolean; minHeight?: number; ariaLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== value) el.innerHTML = value;
  }, [value]);
  const run = (cmd: 'bold' | 'italic' | 'insertUnorderedList') => {
    ref.current?.focus();
    document.execCommand(cmd);
    if (ref.current) onChange(ref.current.innerHTML);
  };
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)', background: 'var(--color-card)' }}>
      <div style={{ display: 'flex', gap: 'var(--sp-1)', padding: 'var(--sp-1) var(--sp-2)', borderBottom: '1px solid var(--color-border)' }}>
        <Button variant="ghost" size="sm" aria-label="Bold" disabled={disabled} onClick={() => run('bold')}><strong>B</strong></Button>
        <Button variant="ghost" size="sm" aria-label="Italic" disabled={disabled} onClick={() => run('italic')}><em>I</em></Button>
        <Button variant="ghost" size="sm" aria-label="Bulleted list" disabled={disabled} onClick={() => run('insertUnorderedList')}>• List</Button>
      </div>
      <div
        ref={ref}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={(e) => onChange((e.target as HTMLDivElement).innerHTML)}
        style={{ minHeight, padding: 'var(--sp-3)', fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-normal, 1.5)', color: 'var(--color-ink)', outline: 'none' }}
      />
    </div>
  );
}
